import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { ChevronLeft, ChevronRight, ExternalLink, Megaphone, Pause, Play, Store } from "lucide-react";
import { trackPartnerAdEvent, useGetPartnerAdsDashboard } from "@workspace/api-client-react";
import { useAuth } from "@/context/auth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export function PartnerAdsDashboardWidget({ className = "mx-4 mb-6" }: { className?: string }) {
  const { user, store } = useAuth();
  const cardRef = useRef<HTMLDivElement>(null);
  const trackedViews = useRef(new Set<string>());
  const [activeIndex, setActiveIndex] = useState(0);
  const [isCardVisible, setIsCardVisible] = useState(false);
  const [autoAdvancePaused, setAutoAdvancePaused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const isManager = user?.role === "admin" || user?.role === "manager";
  const dashboard = useGetPartnerAdsDashboard({
    query: {
      queryKey: ["partner-ads-dashboard", store?.id],
      enabled: Boolean(isManager && store?.id),
      staleTime: 60_000,
      refetchInterval: 5 * 60_000,
    },
    request: { credentials: "include" },
  });
  const ads = dashboard.data?.ads ?? [];
  const offerIds = ads.map(offer => offer.id).join(":");
  const currentIndex = ads.length ? activeIndex % ads.length : 0;
  const ad = ads[currentIndex];

  useEffect(() => {
    setActiveIndex(0);
  }, [store?.id, offerIds]);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);
    updatePreference();
    mediaQuery.addEventListener?.("change", updatePreference);
    return () => mediaQuery.removeEventListener?.("change", updatePreference);
  }, []);

  useEffect(() => {
    if (!ad || !isManager || !store?.id) {
      setIsCardVisible(false);
      return;
    }
    const viewKey = `${store.id}:${ad.id}`;
    const updateVisibility = (visible: boolean) => {
      setIsCardVisible(visible);
      if (visible && !trackedViews.current.has(viewKey)) {
        trackedViews.current.add(viewKey);
        void trackPartnerAdEvent(ad.id, { eventType: "view" }, { credentials: "include" }).catch(() => {});
      }
    };
    const element = cardRef.current;
    if (!element || typeof IntersectionObserver === "undefined") {
      updateVisibility(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      updateVisibility(entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.5));
    }, { threshold: 0.5 });
    observer.observe(element);
    return () => {
      observer.disconnect();
      setIsCardVisible(false);
    };
  }, [ad?.id, isManager, store?.id]);

  const canAutoAdvance = ads.length > 1 && isCardVisible && !autoAdvancePaused &&
    !isHovered && !isFocused && !prefersReducedMotion;
  useEffect(() => {
    if (!canAutoAdvance) return;
    const timer = window.setTimeout(() => {
      setActiveIndex(index => (index + 1) % ads.length);
    }, 8_000);
    return () => window.clearTimeout(timer);
  }, [ads.length, canAutoAdvance, currentIndex]);

  if (!isManager || !store?.id || dashboard.isLoading || dashboard.isError) return null;

  if (!ad) {
    return (
      <Card className={`${className} border-dashed`}>
        <CardContent className="flex flex-wrap items-center justify-between gap-4 p-4">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-primary/10 p-2 text-primary"><Store className="h-5 w-5" aria-hidden="true" /></div>
            <div>
              <p className="font-medium">Partner offers</p>
              <p className="text-sm text-muted-foreground">
                {dashboard.data?.preferences.enabled
                  ? "There are no offers available for your store right now."
                  : "Choose whether to host approved offers from other stores."}
              </p>
            </div>
          </div>
          <Button asChild size="sm" variant="outline"><Link href="/partner-ads">Manage offers</Link></Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      ref={cardRef}
      className={`${className} overflow-hidden border-primary/20`}
      role="region"
      aria-roledescription="carousel"
      aria-label="Partner offers"
      data-testid="region-partner-offers-carousel"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocusCapture={() => setIsFocused(true)}
      onBlurCapture={event => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setIsFocused(false);
      }}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <Badge variant="secondary" className="gap-1"><Megaphone className="h-3 w-3" aria-hidden="true" /> Partner offer</Badge>
          <span className="text-xs text-muted-foreground">From {ad.advertiserName}</span>
        </div>
        <div aria-roledescription="slide" aria-label={`Offer ${currentIndex + 1} of ${ads.length}`} data-testid={`slide-partner-offer-${ad.id}`}>
          <CardTitle className="text-lg" data-testid={`text-partner-offer-title-${ad.id}`}>{ad.title}</CardTitle>
          <CardDescription>{ad.description}</CardDescription>
        </div>
      </CardHeader>
      {ad.imageUrls.length > 0 && (
        <div
          className="grid grid-cols-1 gap-2 px-6 pb-4 sm:grid-cols-2"
          role="group"
          aria-label={`Flyer images for ${ad.title}`}
        >
          {ad.imageUrls.map((imageUrl, index) => (
            <img
              key={imageUrl}
              src={imageUrl}
              alt={`Flyer image ${index + 1} for ${ad.title}`}
              className="aspect-[16/7] w-full rounded-md border bg-muted object-contain"
              loading="lazy"
              decoding="async"
            />
          ))}
        </div>
      )}
      <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-0">
        <span className="text-xs text-muted-foreground">Sponsored offer · Only shown to store managers</span>
        <div className="flex items-center gap-2">
          {ads.length > 1 && (
            <div className="flex items-center gap-1" aria-label="Offer navigation">
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label="Previous partner offer"
                data-testid="button-partner-offer-previous"
                onClick={() => setActiveIndex(index => (index - 1 + ads.length) % ads.length)}
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              </Button>
              <span
                className="min-w-12 text-center text-xs text-muted-foreground"
                aria-live={isFocused ? "polite" : "off"}
                data-testid="status-partner-offer-position"
              >
                {currentIndex + 1} of {ads.length}
              </span>
              {!prefersReducedMotion && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label={autoAdvancePaused ? "Resume automatic offer rotation" : "Pause automatic offer rotation"}
                  data-testid="button-partner-offer-autoplay-toggle"
                  onClick={() => setAutoAdvancePaused(paused => !paused)}
                >
                  {autoAdvancePaused
                    ? <Play className="h-4 w-4" aria-hidden="true" />
                    : <Pause className="h-4 w-4" aria-hidden="true" />}
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label="Next partner offer"
                data-testid="button-partner-offer-next"
                onClick={() => setActiveIndex(index => (index + 1) % ads.length)}
              >
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          )}
          <Button asChild size="sm">
            <a
              href={ad.destinationUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`link-partner-offer-cta-${ad.id}`}
              onClick={() => {
                void trackPartnerAdEvent(ad.id, { eventType: "click" }, { credentials: "include" }).catch(() => {});
              }}
            >
              {ad.ctaLabel}<ExternalLink className="ml-2 h-3.5 w-3.5" aria-hidden="true" />
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
